import React, { useEffect, useRef, useState } from "react";
import "./AriaHologram.css";
import { wsUrl } from "../config/runtime";

/**
 * AriaHologram — rappresentazione visiva dello stato cognitivo di Aria.
 * Fase 0 del Manifesto: NON è un avatar, è la mente che si muove.
 *
 * 8 stati cognitivi: idle / reasoning / working / dialogue / navigation /
 *                    learning / success / error
 *
 * Il componente si connette al WebSocket /api/kernel/aria/activity e
 * cambia comportamento in base allo stato ricevuto.
 */
export default function AriaHologram({ size = 220, attentionTargetRef = null }) {
  const [state, setState] = useState("idle");
  const [target, setTarget] = useState(null);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const wsRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // ── Connessione WebSocket stato cognitivo ─────────────────────
  useEffect(() => {
    let alive = true;
    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl("/api/kernel/aria/activity"));
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.state) setState(msg.state);
            if (msg.attention_target !== undefined) setTarget(msg.attention_target);
          } catch {}
        };
        ws.onclose = () => {
          if (alive) setTimeout(connect, 2000);
        };
      } catch {
        if (alive) setTimeout(connect, 2000);
      }
    };
    connect();
    return () => { alive = false; if (wsRef.current) wsRef.current.close(); };
  }, []);

  // ── Attention: ruota verso il target ──────────────────────────
  useEffect(() => {
    if (!attentionTargetRef || !attentionTargetRef.current) return;
    const t = attentionTargetRef.current.getBoundingClientRect();
    const c = canvasRef.current?.getBoundingClientRect();
    if (!c) return;
    const dx = (t.left + t.width / 2) - (c.left + c.width / 2);
    const dy = (t.top + t.height / 2) - (c.top + c.height / 2);
    const rotY = Math.max(-25, Math.min(25, dx / 30));
    const rotX = Math.max(-15, Math.min(15, -dy / 40));
    setRotate({ x: rotX, y: rotY });
  }, [target, attentionTargetRef]);

  // ── Animazione particelle basata sullo stato ─────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width = size;
    const h = canvas.height = size;
    const cx = w / 2, cy = h / 2;

    // Config per stato
    const cfg = {
      idle:       { count: 60,  speed: 0.4, radius: 0.6, hue: 260, glow: 12, connect: 0.15 },
      reasoning:  { count: 140, speed: 1.6, radius: 0.85, hue: 250, glow: 20, connect: 0.55 },
      working:    { count: 100, speed: 1.2, radius: 0.7, hue: 40,  glow: 18, connect: 0.35 },
      dialogue:   { count: 120, speed: 1.4, radius: 0.75, hue: 200, glow: 22, connect: 0.45 },
      navigation: { count: 130, speed: 2.0, radius: 0.9, hue: 190, glow: 24, connect: 0.5 },
      learning:   { count: 90,  speed: 0.7, radius: 0.65, hue: 280, glow: 16, connect: 0.6 },
      success:    { count: 110, speed: 1.3, radius: 0.85, hue: 140, glow: 26, connect: 0.4 },
      error:      { count: 80,  speed: 1.1, radius: 0.7, hue: 30,  glow: 14, connect: 0.2 },
    }[state] || { count: 60, speed: 0.4, radius: 0.6, hue: 260, glow: 12, connect: 0.15 };

    // Genera particelle
    const particles = Array.from({ length: cfg.count }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const r = (0.3 + Math.random() * 0.7) * (size / 2) * cfg.radius;
      return {
        angle,
        r,
        speed: (0.005 + Math.random() * 0.008) * cfg.speed,
        size: 0.5 + Math.random() * 1.5,
        phase: Math.random() * Math.PI * 2,
      };
    });

    let running = true;
    let t = 0;
    const draw = () => {
      if (!running) return;
      t += 0.016;
      ctx.clearRect(0, 0, w, h);

      // Aura centrale
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, size / 2);
      grad.addColorStop(0, `hsla(${cfg.hue}, 80%, 65%, 0.15)`);
      grad.addColorStop(0.5, `hsla(${cfg.hue}, 80%, 55%, 0.05)`);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Nucleo pulsante (breathing)
      const breath = 1 + Math.sin(t * 0.7) * 0.04;
      const coreR = (size / 8) * breath;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      coreGrad.addColorStop(0, `hsla(${cfg.hue}, 90%, 75%, 0.95)`);
      coreGrad.addColorStop(1, `hsla(${cfg.hue}, 90%, 50%, 0)`);
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      // Particelle
      ctx.globalCompositeOperation = "lighter";
      particles.forEach((p, i) => {
        p.angle += p.speed;
        const wave = Math.sin(t * 2 + p.phase) * 4;
        const x = cx + Math.cos(p.angle) * (p.r + wave);
        const y = cy + Math.sin(p.angle) * (p.r + wave) * 0.8; // ellipse

        ctx.beginPath();
        ctx.fillStyle = `hsla(${cfg.hue + (i % 20) - 10}, 90%, 70%, ${0.5 + Math.sin(t + p.phase) * 0.3})`;
        ctx.shadowBlur = cfg.glow;
        ctx.shadowColor = `hsla(${cfg.hue}, 90%, 60%, 0.8)`;
        ctx.arc(x, y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Connessioni neurali (solo su reasoning/learning/dialogue)
        if (cfg.connect > 0.3 && i > 0 && Math.random() < cfg.connect / 20) {
          const prev = particles[i - 1];
          const px = cx + Math.cos(prev.angle) * prev.r;
          const py = cy + Math.sin(prev.angle) * prev.r * 0.8;
          ctx.beginPath();
          ctx.strokeStyle = `hsla(${cfg.hue}, 90%, 70%, 0.15)`;
          ctx.lineWidth = 0.5;
          ctx.shadowBlur = 0;
          ctx.moveTo(x, y);
          ctx.lineTo(px, py);
          ctx.stroke();
        }
      });
      ctx.globalCompositeOperation = "source-over";
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { running = false; if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [state, size]);

  return (
    <div className="aria-hologram" data-testid="aria-hologram" data-state={state}>
      <canvas
        ref={canvasRef}
        style={{
          transform: `perspective(800px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
          transition: "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
      <div className="aria-hologram-state" data-testid="aria-hologram-state">{state}</div>
    </div>
  );
}
